import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, TEMPLATES } from '../lib/api.js';
import { currentBrand } from '../lib/brand.js';

export function JoinPage(): JSX.Element {
  const navigate = useNavigate();
  const [meetingUrl, setMeetingUrl] = useState('');
  const [templateType, setTemplateType] = useState('general');
  const [title, setTitle] = useState('');
  const [inviteBasecamp, setInviteBasecamp] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);

    const url = meetingUrl.trim();
    if (!url) {
      setError('Paste a meeting URL.');
      return;
    }
    try {
      new URL(url);
    } catch {
      setError('That does not look like a valid URL.');
      return;
    }

    setSubmitting(true);
    try {
      const result = await api.join({
        meetingUrl: url,
        templateType,
        title: title.trim() || undefined,
        ...(inviteBasecamp
          ? { agentSeat: { persona: 'basecamp' as const, speak: true as const, video: true as const } }
          : {}),
      });
      navigate(`/meetings/${result.meetingId}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(
        msg === 'Failed to fetch'
          ? `Could not reach ${currentBrand().name}. Hard-refresh the page; if it asks for a token, paste the API token, then try again.`
          : msg,
      );
      setSubmitting(false);
    }
  }

  return (
    <div>
      <h1 className="page-title">New Meeting</h1>
      <p className="page-subtitle">
        Paste a Zoom, Google Meet, or Microsoft Teams link. {currentBrand().name} will join, record, and summarize.
      </p>

      <form className="join-form" onSubmit={onSubmit}>
        <div className="form-row">
          <label className="form-label" htmlFor="meetingUrl">Meeting URL</label>
          <input
            id="meetingUrl"
            type="url"
            value={meetingUrl}
            onChange={(e) => setMeetingUrl(e.target.value)}
            placeholder="https://us06web.zoom.us/j/123456789"
            autoFocus
            required
          />
          <div className="form-help">Works with Zoom, Google Meet, and Microsoft Teams links.</div>
        </div>

        <div className="form-row">
          <label className="form-label" htmlFor="templateType">Summary template</label>
          <select
            id="templateType"
            value={templateType}
            onChange={(e) => setTemplateType(e.target.value)}
          >
            {TEMPLATES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          <div className="form-help">Claude will tailor the summary to this meeting type.</div>
        </div>

        <div className="form-row">
          <label className="form-label" htmlFor="inviteBasecamp">
            <input
              id="inviteBasecamp"
              type="checkbox"
              checked={inviteBasecamp}
              onChange={(e) => setInviteBasecamp(e.target.checked)}
              style={{ marginRight: 8 }}
            />
            Invite AI with voice and video
          </label>
          <div className="form-help">
            She joins as AI so everyone can hear her and see her. Leave this off to send a silent notetaker. She will not speak or show video until you turn this on.
          </div>
        </div>

        <div className="form-row">
          <label className="form-label" htmlFor="title">Title (optional)</label>
          <input
            id="title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Weekly product sync"
            maxLength={200}
          />
        </div>

        {error && <div className="form-error">{error}</div>}

        <div>
          <button type="submit" className="btn" disabled={submitting}>
            {submitting
              ? <><span className="spinner" /> Dispatching bot…</>
              : inviteBasecamp
                ? 'Invite AI to this meeting'
                : `Send ${currentBrand().name} to this meeting`}
          </button>
        </div>
      </form>
    </div>
  );
}
